import { ApiError } from "@/types/api";

// --- Types ---

export type LoginPayload =
  | { email: string; password: string }
  | { username: string; password: string };

export const ALLOWED_ROLES = [
  "admin",
  "profesional",
  "recepcion",
  "marketing",
] as const;

export type AllowedRole = (typeof ALLOWED_ROLES)[number];

// --- Functions ---

/**
 * Builds the login payload for the backend.
 * Trims the identifier and detects whether it's an email (contains '@') or username.
 */
export function buildLoginPayload(
  identifier: string,
  password: string
): LoginPayload {
  const trimmed = identifier.trim();
  if (trimmed.includes("@")) {
    return { email: trimmed, password };
  }
  return { username: trimmed, password };
}

/**
 * Returns a safe redirect path, rejecting absolute URLs to prevent open-redirect attacks.
 */
export function getSafeNext(nextParam: string | null): string {
  if (!nextParam) return "/";
  if (nextParam.startsWith("http://") || nextParam.startsWith("https://")) {
    return "/";
  }
  return nextParam;
}

/**
 * Maps an unknown error to a user-friendly Spanish message.
 */
export function mapAuthError(err: unknown): string {
  if (err instanceof ApiError) {
    if (err.status === 401) {
      return "Usuario o contraseña incorrectos.";
    }
    if (err.status === 400) {
      return "Datos de acceso inválidos. Verificá el usuario o correo.";
    }
    if (err.status === 422) {
      return "Datos de acceso inválidos. Verificá el usuario o correo.";
    }
    if (err.status === 429) {
      return "Demasiados intentos. Esperá unos minutos antes de volver a intentar.";
    }
    if (err.status >= 500 && err.status <= 599) {
      return "Error del servidor. Intentá de nuevo más tarde.";
    }
    if (err.status === 0) {
      return "Sin conexión. Verificá tu acceso a internet.";
    }
  }
  return "Sin conexión. Verificá tu acceso a internet.";
}

/**
 * Type guard that checks whether a value is a valid AllowedRole.
 */
export function isAllowedRole(value: unknown): value is AllowedRole {
  return ALLOWED_ROLES.includes(value as AllowedRole);
}

/**
 * Checks if a role has administrative level access.
 * Currently, both 'admin' and 'profesional' are considered administrative.
 */
export function hasAdminAccess(rol: AllowedRole | undefined): boolean {
  if (!rol) return false;
  return rol === 'admin' || rol === 'profesional';
}

